import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { FaBox, FaShoppingCart, FaChartLine, FaWallet, FaPlus, FaEye } from 'react-icons/fa';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalOrders: 0,
    totalRevenue: 0,
    creditScore: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [financeData, setFinanceData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      // Fetch orders
      const ordersResponse = await api.get('/orders');
      const orders = ordersResponse.data.orders || [];
      
      // Fetch products if seller
      let productsCount = 0;
      if (user?.userType === 'seller') {
        const productsResponse = await api.get('/products/my-products');
        productsCount = productsResponse.data.products?.length || 0;
        
        // Fetch finance data
        const financeResponse = await api.get('/finance/dashboard');
        setFinanceData(financeResponse.data.finance);
      }
      
      // Calculate stats
      const totalRevenue = orders.reduce((sum, order) => {
        if (user?.userType === 'seller') {
          return sum + (order.status === 'completed' ? order.pricing?.total || 0 : 0);
        }
        return sum + (order.pricing?.total || 0);
      }, 0);

      setStats({
        totalOrders: orders.length,
        totalRevenue,
        totalProducts: productsCount,
        creditScore: user?.creditScore || financeData?.creditHealthScore || 0
      });

      setRecentOrders(orders.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading dashboard...</div>
      </Layout>
    );
  }

  const isSeller = user?.userType === 'seller';

  return (
    <Layout>
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>Welcome back, {user?.businessName || user?.phone}!</h1>
          <p>Here's your {isSeller ? 'business' : 'account'} overview — let's make today count.</p>
        </div>

        <div className="stats-grid">
          <div className="stat-card" onClick={() => navigate('/products')}>
            <div className="stat-icon" style={{ backgroundColor: '#dbeafe' }}>
              <FaBox style={{ color: '#3b82f6' }} />
            </div>
            <div className="stat-content">
              <h3>{stats.totalProducts}</h3>
              <p>{isSeller ? 'Total Products' : 'Products Viewed'}</p>
            </div>
          </div>

          <div className="stat-card" onClick={() => navigate('/orders')}>
            <div className="stat-icon" style={{ backgroundColor: '#d1fae5' }}>
              <FaShoppingCart style={{ color: '#10b981' }} />
            </div>
            <div className="stat-content">
              <h3>{stats.totalOrders}</h3>
              <p>Total Orders</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#fef3c7' }}>
              <FaChartLine style={{ color: '#f59e0b' }} />
            </div>
            <div className="stat-content">
              <h3>₹{stats.totalRevenue.toLocaleString('en-IN')}</h3>
              <p>{isSeller ? 'Total Revenue' : 'Total Spent'}</p>
            </div>
          </div>

          {isSeller && (
            <div className="stat-card" onClick={() => navigate('/finance')}>
              <div className="stat-icon" style={{ backgroundColor: '#e0e7ff' }}>
                <FaWallet style={{ color: '#6366f1' }} />
              </div>
              <div className="stat-content">
                <h3>{stats.creditScore}</h3>
                <p>Credit Health Score</p>
              </div>
            </div>
          )}
        </div>

        {isSeller && financeData && (
          <div className="working-capital-widget card">
            <h2>Working Capital Progress</h2>
            <p>Your progress towards unlocking higher loan limits</p>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${Math.min((financeData.creditHealthScore / 1000) * 100, 100)}%` }}
              />
            </div>
            <div className="progress-info">
              <span>Current Score: {financeData.creditHealthScore}/1000</span>
              <span>{Math.round((financeData.creditHealthScore / 1000) * 100)}%</span>
            </div>
            <div className="loan-eligibility-info">
              <div className="loan-stat">
                <span>Available Limit</span>
                <strong>₹{financeData.loanEligibility?.availableLimit?.toLocaleString('en-IN') || 0}</strong>
              </div>
              <div className="loan-stat">
                <span>Next Tier</span>
                <strong>₹{financeData.loanEligibility?.nextTierLimit?.toLocaleString('en-IN') || 0}</strong>
              </div>
            </div>
            <button className="btn btn-primary" style={{ marginTop: '16px' }} onClick={() => navigate('/finance')}>
              Apply for Instant Credit
            </button>
          </div>
        )}

        <div className="quick-actions card">
          <h2>Quick Actions</h2>
          <div className="actions-grid">
            {isSeller ? (
              <>
                <button className="action-btn" onClick={() => navigate('/products')}>
                  <FaPlus /> Add New Product
                </button>
                <button className="action-btn" onClick={() => navigate('/orders')}>
                  <FaEye /> View Orders
                </button>
                <button className="action-btn" onClick={() => navigate('/finance')}>
                  <FaWallet /> Check Finance Dashboard
                </button>
                <button className="action-btn" onClick={() => navigate('/support')}>
                  📞 Contact Support
                </button>
              </>
            ) : (
              <>
                <button className="action-btn" onClick={() => navigate('/products')}>
                  <FaBox /> Browse Products
                </button>
                <button className="action-btn" onClick={() => navigate('/orders')}>
                  <FaShoppingCart /> Track Orders
                </button>
                <button className="action-btn" onClick={() => navigate('/account-settings')}>
                  👤 View Profile
                </button>
                <button className="action-btn" onClick={() => navigate('/support')}>
                  📞 Contact Support
                </button>
              </>
            )}
          </div>
        </div>

        {recentOrders.length > 0 && (
          <div className="recent-orders card">
            <div className="section-header">
              <h2>Recent Orders</h2>
              <button className="btn btn-outline" onClick={() => navigate('/orders')}>
                View All
              </button>
            </div>
            <div className="orders-table">
              <table>
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>{isSeller ? 'Buyer' : 'Seller'}</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map(order => (
                    <tr key={order._id} onClick={() => navigate(`/orders/${order._id}`)} style={{ cursor: 'pointer' }}>
                      <td><strong>{order.orderNumber}</strong></td>
                      <td>
                        {isSeller 
                          ? order.buyer?.businessName || order.buyer?.phone
                          : order.seller?.businessName || 'Unknown'}
                      </td>
                      <td>₹{order.pricing?.total?.toLocaleString('en-IN')}</td>
                      <td>
                        <span className={`badge ${
                          ['delivered', 'completed'].includes(order.status) ? 'badge-success' :
                          ['shipped', 'in_transit'].includes(order.status) ? 'badge-info' :
                          ['cancelled', 'refunded'].includes(order.status) ? 'badge-danger' :
                          'badge-warning'
                        }`}>
                          {order.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td>{new Date(order.createdAt).toLocaleDateString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
